import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { History } from 'lucide-react';
import { PageHeader } from '@/components/common/PageHeader';
import { LoadingRows, NotImplementedState, ErrorState } from '@/components/common/Feedback';
import { useStubResource, isNotImplemented } from '@/hooks/use-resource';
import { AuditFilters, EMPTY_FILTERS, type AuditFilterValues } from '@/components/audit/AuditFilters';
import { AuditTimeline, type AuditEvent } from '@/components/audit/AuditTimeline';

export const Route = createFileRoute('/_auth/audit')({
  component: AuditScreen,
});

function AuditScreen() {
  const [filters, setFilters] = useState<AuditFilterValues>(EMPTY_FILTERS);

  const { data, isLoading, error } = useStubResource<AuditEvent>('audit');

  const isStub = isNotImplemented(error);
  const isError = error !== null && error !== undefined && !isStub;

  // Client-side filtering (for when data is eventually available)
  const events: AuditEvent[] = (() => {
    const list: AuditEvent[] = data?.data ?? [];
    let filtered = list;
    if (filters.actor) {
      const q = filters.actor.toLowerCase();
      filtered = filtered.filter((ev: AuditEvent) => ev.actor.toLowerCase().includes(q));
    }
    if (filters.resourceType) {
      filtered = filtered.filter((ev: AuditEvent) => ev.resource_type === filters.resourceType);
    }
    if (filters.action) {
      filtered = filtered.filter((ev: AuditEvent) => ev.action.toLowerCase() === filters.action);
    }
    if (filters.dateFrom) {
      const from = new Date(filters.dateFrom).getTime();
      filtered = filtered.filter((ev: AuditEvent) => new Date(ev.timestamp).getTime() >= from);
    }
    if (filters.dateTo) {
      const to = new Date(filters.dateTo + 'T23:59:59').getTime();
      filtered = filtered.filter((ev: AuditEvent) => new Date(ev.timestamp).getTime() <= to);
    }
    return filtered;
  })();

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <PageHeader title="Auditoría" icon={History} description="Registro de actividad y cambios" />

      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Left filter sidebar — always visible, disabled when stub */}
        <AuditFilters
          filters={filters}
          onChange={setFilters}
          disabled={isStub || isLoading}
        />

        {/* Main content area */}
        <main className="flex-1 min-w-0 overflow-y-auto px-4 py-4">
          {isLoading ? (
            <LoadingRows rows={8} />
          ) : isStub ? (
            <NotImplementedState resource="Registro de auditoría" />
          ) : isError ? (
            <ErrorState error={error} resource="Auditoría" />
          ) : (
            <AuditTimeline events={events} />
          )}
        </main>
      </div>
    </div>
  );
}
